import React from 'react';
import ReactCheckboxTree from 'react-checkbox-tree';
import { createGlobalStyle } from 'styled-components';
import _ from 'underscore';

type NodeValue = number | string;
type Node = {
  ['label' | 'value' | 'id' | 'text' | 'name']: NodeValue,
  children?: Array<Node>,
};

type Props = {
  nodes: Array<Node>,
  checked: Array<NodeValue>,
  expanded: Array<NodeValue>,
  checkAll: boolean,
  expandAll: boolean,
  onChange?: (Array<NodeValue>, Array<NodeValue>, Array<NodeValue>) => void,
};

type State = {
  nodes: Array<Node>,
  checked: Array<NodeValue>,
  expanded: Array<NodeValue>,
};

export default class CheckboxTree extends React.Component<Props, State> {
  originalValueMap: { [NodeValue]: NodeValue };

  static defaultProps = {
    nodes: [],
    checked: [],
    expanded: [],
    checkAll: false,
    expandAll: false,
  };

  constructor(props) {
    super(props);

    this.originalValueMap = {}; // Utilized to maintain node value type for `onChange`
    this.state = this.initialState(props);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (JSON.stringify(nextProps.nodes) !== JSON.stringify(this.props.nodes)) {
      this.setState(this.initialState(nextProps));
    }
  }

  initialState = (props: Props): State => {
    this.selectedTopNodes = [];
    const nodes = this.fixKeys(props.nodes);

    return {
      nodes,
      checked: this.initialChecked(nodes),
      expanded: this.initialExpanded(nodes),
    };
  };

  /*
    1. Return every value if checkAll is true
    2. Return checked value passed in props and all it's children
    3. Return empty array if default to nothing
   */
  initialChecked(nodes: Array<Node>): Array<NodeValue> {
    if (this.props.checkAll) {
      return this.getAllValues(nodes);
    } else if (this.props.checked.length > 0) {
      const checkedChildrenIds = this.getAllValues(this.nodesToCheck(nodes));
      const allCheckedNodeIds = this.props.checked.concat(checkedChildrenIds);

      return allCheckedNodeIds.filter(
        (node, pos, self) => self.indexOf(node) === pos
      ); // Filter unique
    } else {
      return [];
    }
  }

  /*
    If passing in a parent node that is checked, this will ensure all children nodes are checked
   */
  nodesToCheck(nodes: Array<Node>): Array<Node> {
    const checkedNodes = nodes.filter(this.isNodeChecked);

    const children = this.childNodes(nodes);
    let childrenNodesToCheck = [];

    if (children.length > 0) {
      childrenNodesToCheck = this.nodesToCheck(children);
    }

    return checkedNodes.concat(childrenNodesToCheck);
  }

  /*
    Expand nodes where the parent is not checked but the children are
   */
  initialExpanded(nodes: Array<Node>): Array<NodeValue> {
    if (this.props.expandAll) {
      return this.getAllValues(nodes);
    } else {
      const nodesToExpand = this.nodesToExpand(nodes),
        nodesToExpandId = nodesToExpand.map((node) => node.value);

      return this.props.expanded.concat(nodesToExpandId);
    }
  }

  nodesToExpand(nodes: Array<Node>): Array<Node> {
    const nodeWithCheckedChildren = nodes.filter((node) => {
      if (node.children) {
        return (
          node.children.filter((child) => {
            return this.props.checked.indexOf(child.value) >= 0;
          }).length > 0
        );
      } else {
        return false;
      }
    });

    const children = this.childNodes(nodes);
    let childrenNodesToExpand = [];

    if (children.length > 0) {
      childrenNodesToExpand = this.nodesToExpand(children);
    }

    return nodeWithCheckedChildren.concat(childrenNodesToExpand);
  }

  /*
    Get every value in this tree. Used for checkAll
   */
  getAllValues(nodes: Array<Node>): Array<NodeValue> {
    if (nodes.length > 0) {
      const nodesValue = nodes.map((node) => node.value);

      return nodesValue.concat(this.getAllValues(this.childNodes(nodes)));
    } else {
      return [];
    }
  }

  childNodes(nodes: Array<Node>): Array<Node> {
    let children = nodes.map((node) => node.children);
    return [].concat(...children).filter((node) => {
      return node !== undefined;
    });
  }

  updateExpand = (expanded: Array<NodeValue>) => {
    this.setState({ expanded });
  };

  render() {
    return (
      <>
        <GlobalStyle />
        <ReactCheckboxTree
          nodes={this.state.nodes}
          checked={this.state.checked}
          expanded={this.state.expanded}
          showNodeIcon={false}
          onCheck={this.onCheck}
          onExpand={this.updateExpand}
        />
      </>
    );
  }

  fixKeys(nodes: Array<Node>): Array<Node> {
    const mapValues = (node: Node) => {
      if (
        node.hasOwnProperty('text') ||
        node.hasOwnProperty('name') ||
        node.hasOwnProperty('id') ||
        node.hasOwnProperty('value')
      ) {
        const valueString = (node.value || node.id).toString();
        if (!this.originalValueMap[valueString]) {
          this.originalValueMap[valueString] = (
            node.value || node.id
          ).valueOf();
        }

        const returnValue = {
          value: valueString,
          label: node.label || node.text || node.name,
        };

        if (Array.isArray(node.children)) {
          returnValue.children = node.children.map(mapValues);
        }

        return returnValue;
      }
    };

    return nodes.map(mapValues);
  }

  /*
    The state only stores the checked leaf nodes. We usually want only the highest most parent node that's checked
    So this will return the top most fully selected node
   */
  nodesFullySelected(
    nodes: Array<Node>,
    checked: Array<NodeValue>
  ): Array<Node> {
    return nodes.filter((node) => {
      if (node.children) {
        const goodNodes = this.nodesFullySelected(node.children, checked);

        if (goodNodes.length === node.children.length) {
          return true;
        } else {
          this.selectedTopNodes = this.selectedTopNodes.concat(goodNodes);

          return false;
        }
      } else {
        return checked.indexOf(node.value.toString()) >= 0;
      }
    });
  }

  leafNodes(nodes: Array<Node>): Array<Node> {
    return _.flatten(
      nodes.map((node) => {
        if (node.children) {
          return this.leafNodes(node.children);
        } else {
          return [node];
        }
      })
    );
  }

  leafNodesSelected(
    nodes: Array<Node>,
    checked: Array<NodeValue>
  ): Array<Node> {
    const leaves = this.leafNodes(nodes);
    return leaves.filter((leaf) => {
      return checked.indexOf(leaf.value) >= 0;
    });
  }

  isNodeChecked = (node: Node): boolean => {
    return this.props.checked.indexOf(this.originalValueMap[node.value]) >= 0;
  };

  onCheck = (checked: Array<NodeValue>) => {
    this.setState({ checked });

    if (this.props.onChange) {
      this.selectedTopNodes = [];
      const topLayerChecked = this.nodesFullySelected(
        this.state.nodes,
        checked
      ).concat(this.selectedTopNodes);
      const topLayerIds = topLayerChecked.map(
        (node) => this.originalValueMap[node.value]
      );
      const checkedLeafIds = this.leafNodesSelected(
        this.state.nodes,
        checked
      ).map((node) => this.originalValueMap[node.value]);

      this.props.onChange(
        topLayerIds,
        checked.map((v) => this.originalValueMap[v]),
        checkedLeafIds
      );
    }
  };
}

const GlobalStyle = createGlobalStyle`
  .react-checkbox-tree {
    font-size: 14px;

    .rct-title {
      padding: 3px 0;
      display: table-cell;
      word-break: break-word;
    }

    .rct-text {
      font-weight: 400;
    }

    .rct-text label {
      display: table;
    }

    input[type="checkbox"] {
      margin-right: 5px;
    }

    button {
      outline: 0;
      border: 0;
      background-color: transparent;
      float: left;
      margin-top: 6px;
      margin-left: 2px;

      .rct-icon {
        display: inline-block;
        width: 0;
        height: 0;

        border-style: solid;
        border-width: 4px 0 4px 6.9px;
        border-color: transparent transparent transparent black;

        &.rct-icon-expand-open {
          transform: rotate(90deg);
          -webkit-transform: rotate(90deg);
        }
      }
    }

    .rct-node {
      &.rct-node-parent {
        margin-left: 0;
        padding-left: 0;
      }

      &.rct-node-leaf {
        margin-left: 0px;
      }

      .rct-node {
        &.rct-node-parent {
          margin-left: 20px
        }
        &.rct-node-leaf {
          margin-left: 35px
        }
      }
    }

    ol {
      list-style-type: none;
      padding-left: 0;
    }

    li {
      padding: 0 5px;
    }
  }
`;
